/**
 * 子表单元格流程 ACL。
 * 流程容器内默认禁用；ACL 就绪后按白名单设 hidden/disabled，editable 只解除流程强制禁（业务禁用仍可收窄）。
 * 只匹配本子表 provide 的 subTableAclConfigs，不按 dataIndex 跨表反查。
 */
import {useDisabledFunHook} from "@/main-page/gridCellEditor/editorCtls/EnumSelectEditor/hooks/useDisabledFunHook";
import {resolveFlowFieldAclState} from "@/main-page-base/ctls/containers/xmForm/hooks/useFormFlowAcl";

export function useSimpleFlowAclHooks({column, record, flowContainer}) {
  const subTableAclConfigs: any = inject('subTableAclConfigs', []);
  const simpleFlowAclConfigs: any = inject('simpleFlowAclConfigs', null);

  const hiddenFlag = ref<boolean | null>(null);
  const editorFlag = ref<boolean | null>(null);
  const readOnlyFlag = ref<boolean | null>(null);

  const isFlowAclReady = function () {
    const cfg = unref(simpleFlowAclConfigs);
    return !!(cfg && cfg.domainTable);
  };

  const initFlowAclConfig = function () {
    if (!unref(flowContainer)) {
      return;
    }

    // 默认禁用；不可把 editorFlag 设 true（会强制可写压过业务 setColDisabled）
    hiddenFlag.value = false;
    readOnlyFlag.value = true;
    editorFlag.value = false;

    if (!isFlowAclReady()) {
      return;
    }

    const configs = findFlowColumnAclConfig({column, subTableAclConfigs});
    if (!configs) {
      return; // 白名单未命中：保持禁用
    }

    const state = resolveFlowFieldAclState(configs);
    if (state === 'hidden') {
      hiddenFlag.value = true;
      readOnlyFlag.value = true;
      editorFlag.value = false;
      return;
    }
    if (state === 'disabled') {
      hiddenFlag.value = false;
      readOnlyFlag.value = true;
      editorFlag.value = false;
      return;
    }
    // editable：解除流程强制禁，走单元格原有 disabled/vuex/disabledFun
    hiddenFlag.value = false;
    readOnlyFlag.value = false;
    editorFlag.value = false;
  }
  initFlowAclConfig();

  watch(() => unref(subTableAclConfigs), () => {
    nextTick(() => {
      initFlowAclConfig()
    })
  })
  watch(() => unref(simpleFlowAclConfigs), () => {
    nextTick(() => {
      initFlowAclConfig()
    })
  })

  const {disabledFun}=useDisabledFunHook({column,record});
  return {
    disabledFun,
    readOnlyFlag,
    hiddenFlag,
    editorFlag
  }
}

/**
 * 在本子表 provide 的 subTableAclConfigs（domainTable[本表业务字段名] 切片）中定位某列的 ACL 条目。
 * 不做跨子表反查：同名业务字段（如生产日期）在其它子表白名单放行时，不得放行本表单元格；
 * 无 provide 或切片为空一律返回 null（默认禁）。
 */
export function findFlowColumnAclConfig({column, subTableAclConfigs}) {
  const col = unref(column);
  const colKeys = [col?.dataIndex, col?.propId].filter((k) => k != null && k !== '');
  const aclConfigs = unref(subTableAclConfigs);
  if (colKeys.length === 0 || !Array.isArray(aclConfigs) || aclConfigs.length === 0) {
    return null;
  }
  return aclConfigs.find((item) =>
    colKeys.indexOf(item.value) >= 0 || colKeys.indexOf(item.dbField) >= 0
  ) || null;
}

/**
 * 流程容器内某列是否被白名单放行可编辑；非流程容器恒为 true。
 * ACL 未就绪或白名单未命中一律 false（默认禁），供单元格渲染漏斗在挂载编辑器前把关。
 */
export function isFlowColumnEditable({column, flowContainer, subTableAclConfigs, simpleFlowAclConfigs}): boolean {
  if (!unref(flowContainer)) {
    return true;
  }
  const cfg = unref(simpleFlowAclConfigs);
  if (!cfg || !cfg.domainTable) {
    return false;
  }
  const configs = findFlowColumnAclConfig({column, subTableAclConfigs});
  if (!configs) {
    return false;
  }
  return resolveFlowFieldAclState(configs) === 'editable';
}
